<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ConfigProvider, TooltipProvider } from 'reka-ui'
import { can, session } from '@/lib/auth'
import { isRtl, type Locale } from '@/lib/i18n'
import { landingFor, workstationLocked } from '@/app/nav'
import { pageHeader } from '@/app/pageHeader'
import { isStandalone, loadWorkstation } from '@/app/router'
import { useFormat } from '@/lib/format'
import Spinner from '@/ui/Spinner.vue'
import ToastRegion from '@/ui/ToastRegion.vue'
import AppShell from '@/app/AppShell.vue'
import GuardedView from '@/app/GuardedView.vue'
import LoginPage from '@/app/pages/LoginPage.vue'
import WorkstationOpening from '@/features/agent/WorkstationOpening.vue'
import InstanceTokens from '@/features/design/InstanceTokens.vue'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const f = useFormat()

/*
 * The platform console, an invitation link, a TV screen: no org session, no
 * shell. Nothing but a spinner shows until the first navigation is resolved:
 * before it the route is still the start location, and a TV opening its
 * address would flash the login screen.
 */
const routerReady = ref(false)
void router.isReady().then(() => { routerReady.value = true })
const standalone = computed(() => routerReady.value && isStandalone(route))

/* Reka components (tabs, menus, tooltips, dialogs) follow the language's direction. */
const dir = computed(() => (isRtl(locale.value as Locale) ? 'rtl' : 'ltr'))

/*
 * The agent workstation is full screen, outside the shell. A locked agent
 * account gets it whatever the address, and it is rendered here rather than
 * by the router so that it is never unmounted: Back or a typed address
 * cannot drop the card of a call in progress. The address is put back to
 * /agent (router.ts does it before any navigation; this covers a login made
 * from a deep link).
 */
const Workstation = defineAsyncComponent({ loader: loadWorkstation, loadingComponent: WorkstationOpening })
const locked = computed(() => Boolean(session.user) && workstationLocked(can))
const workstation = computed(() =>
  locked.value || (route.meta.layout === 'bare' && route.meta.navKey === 'agent' && can('agent.work')))
/* Another full-screen page (a supervisor's preview of the TV wall). */
const bare = computed(() => route.meta.layout === 'bare')
watch([locked, () => route.path], ([isLocked, path]) => {
  if (isLocked && path !== '/agent' && !standalone.value) void router.replace('/agent')
})
/*
 * A full-screen page the account may not open (the workstation without
 * agent.work, the wall preview without monitor.view) would be a dead end
 * outside the shell: back to the home screen instead, also when a renewed
 * session loses the permission.
 */
watch([() => session.user, () => route.fullPath], () => {
  if (!session.user || standalone.value || route.meta.layout !== 'bare' || locked.value) return
  if (route.meta.perm && !can(route.meta.perm)) void router.replace(landingFor(can))
})

// Logging in on "/" sends the account to its home screen; any other URL is kept (deep links survive login).
// Checked after the first navigation: until then the route reads "/" whatever the URL.
watch(() => session.user, async (user) => {
  await router.isReady()
  if (user && route.path === '/') void router.replace(landingFor(can))
})

/* The window title follows the screen and the language, in every layout. */
watchEffect(() => {
  if (standalone.value) return
  // The org's name instead of the product's, once logged in.
  const app = session.org?.name || t('common.appName')
  if (!session.ready) { document.title = app; return }
  if (!session.user) { document.title = `${t('auth.title')} · ${app}`; return }
  const key = route.meta.navKey
  const title = pageHeader.title ?? (key ? t(`nav.${key}.label`) : '')
  document.title = title ? `${title} · ${app}` : app
})
</script>

<template>
  <ConfigProvider :dir="dir">
    <TooltipProvider :delay-duration="250" :skip-delay-duration="400">
      <!-- The org's design tokens, set before any screen: a screen setting them would remove them when left. -->
      <InstanceTokens v-if="session.user && !standalone" />
      <div v-if="!routerReady" class="grid h-full place-items-center"><Spinner /></div>
      <RouterView v-else-if="standalone" />
      <div v-else-if="!session.ready" class="grid h-full place-items-center"><Spinner /></div>
      <!-- Not a route: the login replaces whatever URL was asked for, which renders once logged in. -->
      <LoginPage v-else-if="!session.user" />
      <Workstation v-else-if="workstation" />
      <GuardedView v-else-if="bare" />
      <AppShell v-else />
      <!-- Fixed, outside the layout: the full-screen workstation keeps its no-scroll fit. -->
      <div v-if="session.support && session.user" role="status"
           class="fixed bottom-3 start-1/2 z-toast -translate-x-1/2 whitespace-nowrap rounded-pill bg-warning px-4 py-1.5 text-[12.5px] font-medium text-white shadow-pop rtl:translate-x-1/2">
        <i18n-t keypath="shell.support.mark" tag="span" scope="global">
          <template #by>{{ session.support.by }}</template>
          <template #user><b>{{ session.user.username }}</b></template>
        </i18n-t>
        <template v-if="session.support.until"> · {{ t('shell.support.until', { time: f.time(session.support.until) }) }}</template>
      </div>
      <ToastRegion />
    </TooltipProvider>
  </ConfigProvider>
</template>
